export interface TranslationItemDto {
	languageCode: string;
	text: string;
	isMachineTranslation?: boolean;
}

export enum GoodYouDoTypeEnum {
	ConservationAndWildlife = 'ConservationAndWildlife',
	LocalCommunityImpact = 'LocalCommunityImpact',
	LearningAndSkills = 'LearningAndSkills',
	EmploymentAndWellBeing = 'EmploymentAndWellBeing',
	OperatingSustainably = 'OperatingSustainably',
	Other = 'Other'
}

export const GoodYouDoTypeLabels: Record<GoodYouDoTypeEnum, string> = {
	[GoodYouDoTypeEnum.ConservationAndWildlife]: 'Conservation & Wildlife',
	[GoodYouDoTypeEnum.LocalCommunityImpact]: 'Local Community Impact',
	[GoodYouDoTypeEnum.LearningAndSkills]: 'Learning & Skills',
	[GoodYouDoTypeEnum.EmploymentAndWellBeing]: 'Employment & Well-being',
	[GoodYouDoTypeEnum.OperatingSustainably]: 'Operating Sustainably',
	[GoodYouDoTypeEnum.Other]: 'Other'
};

export const GoodYouDoTypeDescriptions: Record<GoodYouDoTypeEnum, string> = {
	[GoodYouDoTypeEnum.ConservationAndWildlife]:
		'Protecting habitats, nurture biodiversity, and safeguarding wildlife',
	[GoodYouDoTypeEnum.LocalCommunityImpact]:
		'Supporting resilient, thriving, and culturally rich communities',
	[GoodYouDoTypeEnum.LearningAndSkills]:
		'Unlocking potential, training future leaders and inspiring lifelong learning',
	[GoodYouDoTypeEnum.EmploymentAndWellBeing]:
		'Creating meaningful work, caring for your people, and nurturing successful teams',
	[GoodYouDoTypeEnum.OperatingSustainably]:
		'Minimising your footprint, using resources intentionally and championing sustainability',
	[GoodYouDoTypeEnum.Other]: ''
};

export interface GoodYouDoMediaItemDto {
	id?: number;
	blobRelativePath: string;
	mediaType: string;
	originalFileName: string;
	width: number;
	height: number;
	fileSize: number;
	fileType: string;
	sequence: number;
	credit: string;
	visibility: number;
	/** Display title for the media item (shown in the uploader row and iBrochure). */
	name?: TranslationItemDto[];
	/** Optional long-form description for the media item. */
	description?: TranslationItemDto[];
	/** Normalised YouTube/Vimeo iframe src. Only populated when mediaType === 'EmbedVideo'. */
	embedUrl?: string;
	existsInDb?: boolean;
	isMarkedForDeletion?: boolean;
}

export interface GoodYouDoProjectResponseDto {
	id: number;
	profileId: number;
	name: TranslationItemDto[];
	mission: TranslationItemDto[];
	impact: TranslationItemDto[];
	types: string[];
	campaignLink: string;
	media: GoodYouDoMediaItemDto[];
	sequence: number;
}

export interface CreateGoodYouDoProjectDto {
	name: TranslationItemDto[];
	mission: TranslationItemDto[];
	impact: TranslationItemDto[];
	types: string[];
	campaignLink: string;
	media: GoodYouDoMediaItemDto[];
	isProfilePublished: boolean;
	isMachineTranslationsEnabled: boolean;
}

export interface UpdateGoodYouDoProjectDto {
	id: number;
	name: TranslationItemDto[];
	mission: TranslationItemDto[];
	impact: TranslationItemDto[];
	types: string[];
	campaignLink: string;
	media: GoodYouDoMediaItemDto[];
	sequence: number;
	isProfilePublished: boolean;
	isMachineTranslationsEnabled: boolean;
}

export interface UpdateGoodYouDoSequenceDto {
	sequence: number;
}

export function resolveGoodYouDoMediaBlobPath(
	_profileId: number | undefined | null,
	media: Pick<GoodYouDoMediaItemDto, 'id' | 'originalFileName'> & { blobRelativePath?: string }
): string {
	const trimmed = (media.blobRelativePath || '').trim();
	if (!trimmed) return '';
	const lower = trimmed.toLowerCase();
	if (lower.startsWith('data:') || lower.startsWith('http://') || lower.startsWith('https://')) {
		return '';
	}
	return trimmed;
}

function inferGoodYouDoFileTypeFromOriginalName(
	originalFileName: string | undefined,
	mediaType: string | undefined
): string {
	const ext = (originalFileName || '').split('.').pop()?.toLowerCase() || '';
	const mt = (mediaType || '').toLowerCase();
	const isVideo = mt === 'video';
	if (isVideo) {
		const v: Record<string, string> = {
			mp4: 'video/mp4',
			webm: 'video/webm',
			mov: 'video/quicktime',
			m4v: 'video/x-m4v'
		};
		return v[ext] || 'video/mp4';
	}
	const i: Record<string, string> = {
		jpg: 'image/jpeg',
		jpeg: 'image/jpeg',
		png: 'image/png',
		gif: 'image/gif',
		webp: 'image/webp',
		bmp: 'image/bmp',
		svg: 'image/svg+xml'
	};
	return i[ext] || 'image/jpeg';
}

/** Hydrate API media for UI. Trust the server's `blobRelativePath` verbatim — we never fabricate
 * it here (see `resolveGoodYouDoMediaBlobPath`). Only infer `fileType` if the server omitted it so
 * FileUploader can render previews. */
export function normalizeGoodYouDoMediaItemFromApi(
	_profileId: number,
	raw: Partial<GoodYouDoMediaItemDto> & Record<string, unknown>
): GoodYouDoMediaItemDto & Record<string, unknown> {
	const fileType =
		(raw.fileType as string) ||
		inferGoodYouDoFileTypeFromOriginalName(
			raw.originalFileName as string | undefined,
			raw.mediaType as string | undefined
		);
	return {
		...raw,
		fileType
	} as GoodYouDoMediaItemDto & Record<string, unknown>;
}

export function normalizeGoodYouDoProjectFromApi(
	profileId: number,
	project: GoodYouDoProjectResponseDto | null | undefined
): GoodYouDoProjectResponseDto | null | undefined {
	if (project === undefined || project === null) return project;
	return {
		...project,
		media: (project.media || []).map((m) =>
			normalizeGoodYouDoMediaItemFromApi(
				profileId,
				m as Partial<GoodYouDoMediaItemDto> & Record<string, unknown>
			)
		)
	};
}

export function normalizeGoodYouDoProjectsFromApi(
	profileId: number,
	projects: GoodYouDoProjectResponseDto[] | null | undefined
): GoodYouDoProjectResponseDto[] {
	if (!projects?.length) return [];
	return projects.map(
		(p) => normalizeGoodYouDoProjectFromApi(profileId, p) as GoodYouDoProjectResponseDto
	);
}
